/**
 * Shoppable Videos - TikTok/Instagram Reels Style
 * Handles video playback, swipe navigation, and product tags
 */

(function() {
  'use strict';

  // ========== CONFIG ==========
  const CONFIG = {
    playerVars: {
      autoplay: 1,
      controls: 1,
      modestbranding: 1,
      rel: 0,
      playsinline: 1,
      enablejsapi: 1,
      origin: window.location.origin
    },
    swipeThreshold: 50, // Minimum distance for swipe
    productDisplayDuration: 10000 // How long to show product tags (ms)
  };

  // ========== STATE ==========
  let state = {
    player: null,
    currentVideoIndex: 0,
    videos: [],
    isModalOpen: false,
    isMuted: false,
    isYouTubeAPIReady: false,
    productTimers: {},
    isFromStoriesSection: false // NEW: Track if video is from stories section
  };

  // ========== YOUTUBE API SETUP ==========
  // Only set callback if not already defined (in case multiple sections load)
  if (!window.onYouTubeIframeAPIReady) {
    window.onYouTubeIframeAPIReady = function() {
      state.isYouTubeAPIReady = true;
    };
  } else {
    // Callback already exists, check if API is ready
    if (typeof YT !== 'undefined' && typeof YT.Player !== 'undefined') {
      state.isYouTubeAPIReady = true;
    }
  }

  // ========== INITIALIZATION ==========
  function init() {
    // Check if YouTube API is already loaded
    if (typeof YT !== 'undefined' && typeof YT.Player !== 'undefined') {
      state.isYouTubeAPIReady = true;
    }
    
    collectVideos();
    attachEventListeners();
    checkForAutoplay();
  }

  // Collect all videos from the page
  function collectVideos() {
    const thumbnails = document.querySelectorAll('.shoppable-video-thumbnail');
    
    state.videos = Array.from(thumbnails).map((thumb, index) => {
      const youtubeId = thumb.dataset.youtubeId;
      const imageUrl = thumb.dataset.imageUrl;
      const storyType = thumb.dataset.storyType;
      const videoTitle = thumb.dataset.videoTitle || '';
      const products = collectProducts(thumb);
      
      // Check if this video is from the stories section
      const isFromStories = thumb.closest('.product-video-stories') !== null;
      
      return {
        index,
        youtubeId,
        imageUrl,
        storyType,
        videoTitle,
        products,
        element: thumb,
        isFromStories
      };
    });
  }

  // Collect products from video element
  function collectProducts(thumbnailElement) {
    const productElements = thumbnailElement.querySelectorAll('.video-product');
    return Array.from(productElements).map(el => ({
      handle: el.dataset.productHandle,
      title: el.dataset.productTitle,
      price: el.dataset.productPrice,
      url: el.dataset.productUrl,
      image: el.dataset.productImage,
      variantId: el.dataset.productId,
      available: el.dataset.available === 'true',
      timestamp: parseInt(el.dataset.timestamp) || 0
    }));
  }

  // Attach all event listeners
  function attachEventListeners() {
    // Video thumbnail clicks
    document.querySelectorAll('.shoppable-video-thumbnail').forEach((thumb, index) => {
      thumb.addEventListener('click', () => openModal(index));
    });

    // Modal controls
    const modal = document.getElementById('shoppable-video-modal');
    const closeBtn = document.querySelector('.shoppable-video-close');
    const muteBtn = document.querySelector('.shoppable-video-mute');
    const prevBtn = document.querySelector('.shoppable-video-prev');
    const nextBtn = document.querySelector('.shoppable-video-next');

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (muteBtn) muteBtn.addEventListener('click', toggleMute);
    
    // Arrow buttons work on mobile AND desktop
    if (prevBtn) prevBtn.addEventListener('click', () => navigateVideo(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => navigateVideo(1));

    // Click outside modal to close
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });
    }

    // Keyboard navigation (desktop only)
    document.addEventListener('keydown', handleKeyboard);
  }

  // Check if there's a URL parameter to autoplay
  function checkForAutoplay() {
    const urlParams = new URLSearchParams(window.location.search);
    const videoIndex = urlParams.get('video');
    if (videoIndex !== null) {
      const index = parseInt(videoIndex);
      if (index >= 0 && index < state.videos.length) {
        openModal(index);
      }
    }
  }

  // ========== MODAL MANAGEMENT ==========
  function openModal(videoIndex) {
    if (videoIndex < 0 || videoIndex >= state.videos.length) return;

    state.currentVideoIndex = videoIndex;
    state.isModalOpen = true;

    const modal = document.getElementById('shoppable-video-modal');
    if (!modal) return;

    // Track if this video is from stories section
    state.isFromStoriesSection = state.videos[videoIndex].isFromStories;

    // ========== ANALYTICS TRACKING ==========
    const currentStory = state.videos[videoIndex];
    trackStoryView(currentStory, videoIndex);

    // Prevent body scroll
    document.body.classList.add('shoppable-video-modal-open');

    // Show modal with animation
    modal.classList.add('active');

    // Load video
    loadVideo(state.videos[videoIndex]);

    // Update URL
    updateURL();
  }

  // Track story views with multiple analytics platforms
  function trackStoryView(story, index) {
    const storyType = story.storyType || (story.youtubeId ? 'video' : 'image');
    const storyId = story.youtubeId || story.imageUrl || 'unknown';
    const isFromStories = story.isFromStories;
    
    // Get product info if available
    const productHandle = story.products?.[0]?.handle || 'unknown';
    
    // Event data
    const eventData = {
      story_type: storyType,
      story_id: storyId,
      story_index: index,
      story_section: isFromStories ? 'product_stories' : 'video_grid',
      product_handle: productHandle,
      story_title: story.videoTitle || 'Untitled'
    };

    // 1. Google Analytics (GA4)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'story_view', {
        event_category: 'Stories',
        event_label: `${storyType}_${index}`,
        story_type: storyType,
        story_section: eventData.story_section,
        product: productHandle
      });
    }

    // 1b. Video attribution - sm-video-attribution.js carries this onto the
    // order, so a story that sells can be told from one that is merely watched.
    if (story.youtubeId) {
      try {
        document.dispatchEvent(new CustomEvent('sm:video-product-click', {
          detail: {
            video: story.youtubeId,
            title: story.videoTitle || '',
            url: productHandle && productHandle !== 'unknown' ? '/products/' + productHandle : '',
            placement: 'story'
          }
        }));
      } catch (e) {}
    }

    // 2. Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'StoryView', {
        content_type: storyType,
        content_ids: [storyId],
        story_index: index
      });
    }

    // 3. Shopify Analytics
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.lib.track('Story Viewed', eventData);
    }

    // 4. Custom DataLayer (works with Google Tag Manager)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'story_view',
      story_type: storyType,
      story_id: storyId,
      story_index: index,
      story_section: eventData.story_section,
      product_handle: productHandle
    });

    // 5. Console log for debugging (remove in production if needed)
    console.log('📊 Story View Tracked:', eventData);
  }

  function closeModal() {
    const modal = document.getElementById('shoppable-video-modal');
    if (!modal) return;

    state.isModalOpen = false;
    state.isFromStoriesSection = false;

    // Destroy player
    if (state.player) {
      state.player.destroy();
      state.player = null;
    }

    // Clear product timers
    clearProductTimers();

    // Hide modal
    modal.classList.remove('active');

    // Restore body scroll
    document.body.classList.remove('shoppable-video-modal-open');

    // Clear URL parameter
    const url = new URL(window.location);
    url.searchParams.delete('video');
    window.history.replaceState({}, '', url);
  }

  // ========== VIDEO/IMAGE LOADING ==========
  function loadVideo(video) {
    if (!video) return;

    // Check if it's an image story
    const thumbnail = video.element;
    const storyType = thumbnail?.dataset.storyType;
    const imageUrl = thumbnail?.dataset.imageUrl;

    if (storyType === 'image' && imageUrl) {
      // Load image viewer
      loadImage(imageUrl);
      return;
    }

    // Load video player
    if (!video.youtubeId) return;

    // Wait for API if not ready
    if (!state.isYouTubeAPIReady) {
      setTimeout(() => loadVideo(video), 100);
      return;
    }

    // Clear any existing product tags
    clearProductTags();
    clearProductTimers();

    // Destroy existing player
    if (state.player) {
      state.player.destroy();
    }

    // Create new player
    const playerContainer = document.getElementById('shoppable-video-player');
    if (!playerContainer) return;

    // Show mute button for videos, hide for images
    const muteBtn = document.querySelector('.shoppable-video-mute');
    if (muteBtn) muteBtn.style.display = 'block';

    state.player = new YT.Player('shoppable-video-player', {
      videoId: video.youtubeId,
      playerVars: CONFIG.playerVars,
      events: {
        onReady: (event) => onPlayerReady(event, video),
        onStateChange: onPlayerStateChange
      }
    });
  }

  function loadImage(imageUrl) {
    // Hide mute button for images
    const muteBtn = document.querySelector('.shoppable-video-mute');
    if (muteBtn) muteBtn.style.display = 'none';

    // Clear existing player
    if (state.player) {
      state.player.destroy();
      state.player = null;
    }

    // Show image in viewer
    const playerContainer = document.getElementById('shoppable-video-player');
    if (!playerContainer) return;

    playerContainer.innerHTML = `
      <div class="shoppable-image-viewer">
        <img src="${imageUrl}" alt="Product Image" class="shoppable-story-image">
      </div>
    `;
  }

  function onPlayerReady(event, video) {
    // Set mute state
    if (state.isMuted) {
      event.target.mute();
    } else {
      event.target.unMute();
    }

    // NEW: Only show product cards if NOT from stories section
    if (!state.isFromStoriesSection) {
      setupProductTimings(video);
    }

    // Try to play (mobile may require user interaction)
    try {
      event.target.playVideo();
    } catch (e) {
      // Autoplay prevented, user will need to click play
    }
  }

  function onPlayerStateChange(event) {
    // Handle state changes
    if (event.data === YT.PlayerState.ENDED) {
      // Track video completion
      const currentStory = state.videos[state.currentVideoIndex];
      trackStoryCompletion(currentStory, state.currentVideoIndex);
      
      // Auto-advance to next video
      navigateVideo(1);
    }
  }

  // Track when story is completed/finished
  function trackStoryCompletion(story, index) {
    const storyType = story.storyType || (story.youtubeId ? 'video' : 'image');
    const storyId = story.youtubeId || story.imageUrl || 'unknown';
    const productHandle = story.products?.[0]?.handle || 'unknown';

    // Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'story_complete', {
        event_category: 'Stories',
        event_label: `${storyType}_${index}`,
        story_type: storyType,
        product: productHandle
      });
    }

    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'StoryComplete', {
        content_type: storyType,
        content_ids: [storyId]
      });
    }

    // DataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'story_complete',
      story_type: storyType,
      story_id: storyId,
      story_index: index,
      product_handle: productHandle
    });

    console.log('✅ Story Completed:', storyType, index);
  }

  // ========== PRODUCT TAG MANAGEMENT ==========
  function setupProductTimings(video) {
    if (!video.products || video.products.length === 0) return;

    video.products.forEach(product => {
      const timer = setTimeout(() => {
        showProductTag(product);
      }, product.timestamp * 1000);

      state.productTimers[product.handle] = timer;
    });
  }

  function showProductTag(product) {
    const container = document.querySelector('.shoppable-video-products');
    if (!container) return;

    // Create product tag with Add to Cart button
    const tag = document.createElement('div');
    tag.className = 'shoppable-product-tag';
    tag.innerHTML = `
      ${product.image ? `<img src="${product.image}" alt="${product.title}" class="shoppable-product-image">` : ''}
      <div class="shoppable-product-info">
        <p class="shoppable-product-price">${product.price}</p>
      </div>
      <button class="shoppable-add-to-cart" data-variant-id="${product.variantId}" ${!product.available ? 'disabled' : ''}>
        ${product.available ? `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          <span>Add to Cart</span>
        ` : '<span>Sold Out</span>'}
      </button>
    `;

    // Add click handler for Add to Cart button
    if (product.available) {
      const addButton = tag.querySelector('.shoppable-add-to-cart');
      addButton.addEventListener('click', (e) => {
        e.stopPropagation();
        handleAddToCart(product.variantId, addButton);
      });
    }

    container.appendChild(tag);

    // Auto-hide after duration
    setTimeout(() => {
      tag.classList.add('hidden');
      setTimeout(() => tag.remove(), 300);
    }, CONFIG.productDisplayDuration);
  }

  // Handle Add to Cart
  async function handleAddToCart(variantId, button) {
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('adding');
    
    const originalContent = button.innerHTML;
    button.innerHTML = `
      <div class="shoppable-cart-spinner"></div>
      <span>Adding...</span>
    `;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();

      // Success state
      button.classList.remove('adding');
      button.classList.add('added');
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <span>Added!</span>
      `;

      // Update cart immediately - Dawn theme compatible
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          // Update cart count bubble
          const cartBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
          if (cartBubble) {
            cartBubble.textContent = cart.item_count;
            if (cart.item_count > 0) {
              document.querySelector('#cart-icon-bubble')?.classList.remove('hidden');
            }
          }
          
          // Trigger Shopify theme cart update event
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
          
          // Also publish for Dawn theme specifically
          if (window.publish) {
            window.publish('cart:item-added', data);
          }
        });

      // Reset button
      setTimeout(() => {
        button.classList.remove('added');
        button.innerHTML = originalContent;
        button.disabled = false;
      }, 2000);

    } catch (error) {
      button.classList.remove('adding');
      button.innerHTML = '<span>Try Again</span>';
      
      setTimeout(() => {
        button.innerHTML = originalContent;
        button.disabled = false;
      }, 2000);
    }
  }

  function clearProductTags() {
    const container = document.querySelector('.shoppable-video-products');
    if (container) {
      container.innerHTML = '';
    }
  }

  function clearProductTimers() {
    Object.values(state.productTimers).forEach(timer => clearTimeout(timer));
    state.productTimers = {};
  }

  // ========== NAVIGATION ==========
  function navigateVideo(direction) {
    let newIndex = state.currentVideoIndex + direction;

    // Loop around
    if (newIndex < 0) {
      newIndex = state.videos.length - 1;
    } else if (newIndex >= state.videos.length) {
      newIndex = 0;
    }

    // Immediately stop current video
    if (state.player) {
      try {
        state.player.stopVideo();
      } catch (e) {
        // Player not ready
      }
    }

    // NEW: Update stories section flag for new video
    state.isFromStoriesSection = state.videos[newIndex].isFromStories;

    state.currentVideoIndex = newIndex;
    loadVideo(state.videos[newIndex]);
    updateURL();
  }

  function updateURL() {
    const url = new URL(window.location);
    url.searchParams.set('video', state.currentVideoIndex);
    window.history.replaceState({}, '', url);
  }

  // ========== MUTE CONTROL ==========
  function toggleMute() {
    state.isMuted = !state.isMuted;

    if (state.player) {
      if (state.isMuted) {
        state.player.mute();
      } else {
        state.player.unMute();
      }
    }

    // Update button icons
    const muteIcon = document.querySelector('.mute-icon');
    const unmuteIcon = document.querySelector('.unmute-icon');
    
    if (muteIcon && unmuteIcon) {
      if (state.isMuted) {
        muteIcon.style.display = 'none';
        unmuteIcon.style.display = 'block';
      } else {
        muteIcon.style.display = 'block';
        unmuteIcon.style.display = 'none';
      }
    }
  }

  // ========== KEYBOARD EVENTS ==========
  function handleKeyboard(e) {
    if (!state.isModalOpen) return;

    switch(e.key) {
      case 'Escape':
        closeModal();
        break;
      case 'ArrowUp':
        e.preventDefault();
        navigateVideo(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        navigateVideo(1);
        break;
      case 'ArrowLeft':
        navigateVideo(-1);
        break;
      case 'ArrowRight':
        navigateVideo(1);
        break;
      case 'm':
      case 'M':
        toggleMute();
        break;
    }
  }

  // ========== START ==========
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinitialize on Shopify section reload
  document.addEventListener('shopify:section:load', init);

})();